import Link from 'next/link';
import { useRouter } from 'next/router';
import { ReactNode, useCallback, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import styled from 'styled-components';
import { palette } from 'styled-tools';

import { AccessibleOnChange, FontWeights, FormInput, maxWidth, Text } from '@calm-web/design-system';

import Loader from '@/components/Loader';
import PurchaseForm from '@/components/purchase/PurchaseForm';
import { OnGenericPaymentSubmit } from '@/components/purchase/PurchaseForm/types';
import { useAnalytics } from '@/hooks/analytics';
import { Gift, GIFT_ANALYTICS_PREFIX, useGiftPurchase } from '@/hooks/purchase/useGiftPurchase';
import { usePaymentFormValues } from '@/hooks/purchase/usePaymentFormValues';
import { usePurchaseSuccess } from '@/hooks/purchase/usePurchaseSuccess';
import {
	useAppLoadedState,
	useBrowserLanguageState,
	usePricesState,
	usePurchaseParamsState,
	useUserState,
} from '@/hooks/store';
import { useComponentDidMount } from '@/hooks/utils/useComponentDidMount';
import getParamAsString from '@/utils/getParamsAsString';
import { Cents } from '@/utils/prices/types';
import { validate } from '@/utils/ui/validator';

import PaymentElementsForm from '../purchase/PaymentElementsForm';
import { createSuccessEventProps } from '../purchase/PaymentElementsForm/utils';
import { useStripeElements } from '../purchase/StripeElementsProvider';
import B2bUpsell from './B2bUpsell';
import PurchaseConfirmation from './PurchaseConfirmation';
import QuantitySelector from './QuantitySelector';

const FullWidth = styled.div`
	margin-top: 3rem;
	width: 100%;
`;

const CenteredRow = styled.div`
	align-items: center;
`;

const Divider = styled.div`
	width: 100%;
	border-bottom: 1px solid ${palette('gray2')};
	margin: 1.5rem 0;
	margin-bottom: 1rem;
`;

const SuccessColumn = styled.div`
	max-width: 500px;
	margin: 0 auto;
`;

const TermsMessage = styled.div`
	font-size: 15px;
	line-height: 1.5;
	margin-top: 2.33rem;
	padding-bottom: 7.44rem;
	${maxWidth('mobileLarge')} {
		font-size: 17px;
		margin-top: 10px;
		padding: 15px 5px;
		border-radius: 5px;
		padding-bottom: 0;
	}
`;

const messages = defineMessages({
	submitText: {
		id: 'giftCertificate.purchaseForm.submitText',
		defaultMessage: 'Purchase Gift',
		description: 'Button text for the form where they purchase gift cards',
	},
	lifetimeSubmitText: {
		id: 'giftCertificate.purchaseForm.lifetimeSubmitText',
		defaultMessage: 'Give Calm for life',
		description: 'Button text for the form where they purchase gift cards',
	},
	afterPurchase: {
		id: 'giftcard.afterPurchase',
		defaultMessage:
			"After purchase, you'll receive gift certificates in your inbox to send to your loved ones",
		description: 'Information on how the gift card email will work after purchase',
	},
	provideDetails: {
		id: 'redesign.purhcase.creditCardForm.provideDetails',
		defaultMessage: 'Provide your details',
		description: 'Header above the form where they purchase gift cards',
	},
	purchaseTerms: {
		id: 'giftCertificate.purchaseForm.terms',
		defaultMessage:
			'By purchasing a Calm gift card, you agree to the <termslink>Gift Card Terms and Conditions</termslink>',
		description: 'Terms and conditions for the gift card purchase',
	},
	email: {
		id: 'giftCertificate.purchaseForm.email',
		defaultMessage: 'Email',
		description: 'CC Email Prompt',
	},
	emptyEmailError: {
		id: 'generics.emptyEmail',
		defaultMessage: 'Email is required!',
		description: 'CC Email Required',
	},
	emailError: {
		id: 'giftCertificate.purchaseForm.emailError',
		defaultMessage: 'Please enter a valid email address.',
		description: 'CC Email Error',
	},
	yourEmail: {
		id: 'generics.yourEmail',
		defaultMessage: 'Your Email',
		description: 'CC Your Email',
	},
});

const minQuantity = 1;

interface PaymentFormProps {
	originalPrice?: Cents | null;
	price: Cents;
	offerFreeCount: number; // buy n get 1 free
	giftType?: 'annual' | 'lifetime';
}

const PaymentForm = ({ originalPrice, price, offerFreeCount, giftType = 'annual' }: PaymentFormProps) => {
	const { isStripeNextEnabled } = useStripeElements();
	const { query } = useRouter();
	const purchaseParams = usePurchaseParamsState();
	const browserLanguage = useBrowserLanguageState();
	const prices = usePricesState();
	const user = useUserState();
	const appLoaded = useAppLoadedState();
	const coupon = getParamAsString(query.coupon);
	const [quantity, setQuantity] = useState(minQuantity);
	const [showB2bUpsell, setShowB2bUpsell] = useState(false);
	const { logEvent, logEventAsync, logPurchase } = useAnalytics();
	const { formatMessage } = useIntl();
	const [didSucceed, setDidSucceed] = useState(false);
	const [gifts, setGifts] = useState<Gift[] | null>(null);
	const [email, setEmail] = useState(user?.email ?? '');
	const [emailError, setEmailError] = useState<string>();
	const onPurchaseSuccess = usePurchaseSuccess();
	const submitGiftPurchase = useGiftPurchase();
	const { paymentFormCurrency } = usePaymentFormValues();

	useComponentDidMount(() => {
		logEvent({
			eventName: 'Gift : Purchase : Landed',
			eventProps: {
				is_stripe_next: isStripeNextEnabled,
			},
		});
	});

	const onChangeQuantity = (_quantity: number): void => {
		setQuantity(_quantity);
		logEvent({
			eventName: 'Gift : Purchase : Quantity : Changed',
			eventProps: {
				new_quantity: _quantity,
				is_stripe_next: isStripeNextEnabled,
			},
		});

		const B2B_SELF_SERVE_MIN = 5;
		setShowB2bUpsell(_quantity >= B2B_SELF_SERVE_MIN);
	};

	const isSubmittable = () => Boolean(email?.length > 0 && !emailError);

	const _onPurchaseSuccess = async (
		response: Record<string, unknown>,
		purchaseArgs: Record<string, unknown>,
	) => {
		const { gifts } = response as { gifts: Gift[] };
		const { paymentMethod } = purchaseArgs as { paymentMethod: string };

		if (!gifts || !paymentMethod) return;

		const productInfo = {
			name: 'Gift of Calm',
			sku: 'gift_card',
			unit_price: price,
			quantity,
		};

		const transactionInfo = { id: gifts[0]?.code };

		logPurchase({ productInfo, transactionInfo });

		const successEventProps = createSuccessEventProps({
			productInfo,
			prices,
			purchaseParams,
			selectedPaymentType: paymentMethod,
			paymentFormCurrency,
			browserLanguage,
			userEmail: user?.email,
			successArgs: {
				is_stripe_next: isStripeNextEnabled,
			},
		});

		await logEventAsync({
			eventName: `${GIFT_ANALYTICS_PREFIX} : Success`,
			eventProps: successEventProps,
		});

		setGifts(gifts);
		setDidSucceed(true);
	};

	const onSubmitPayment: OnGenericPaymentSubmit = useCallback(
		async ({ cardInfo, userInfo, requestInfo, paymentType, ...args }) => {
			logEvent({
				eventName: 'Gift : Purchase : Purchasing',
				eventProps: {
					quantity,
					is_stripe_next: isStripeNextEnabled,
					...(coupon ? { coupon } : {}),
				},
			});

			const result = await submitGiftPurchase({
				...args,
				cardInfo,
				userInfo,
				requestInfo,
				quantity,
				coupon,
				email,
				type: giftType === 'lifetime' ? 'lifetime' : 'annual',
			});

			if (result?.error) {
				return result;
			}
			if (result.success) {
				const giftResponse = result.success.gifts;
				await onPurchaseSuccess({
					paymentType,
					transactionId: giftResponse[0]?.code,
					analyticsPrefix: GIFT_ANALYTICS_PREFIX,
					productInfo: {
						name: 'Gift of Calm',
						sku: 'gift_card',
						unit_price: price,
						quantity,
					},
					newUser: user,
				});
				setGifts(giftResponse);
			}
			return result;
		},
		[
			coupon,
			email,
			giftType,
			isStripeNextEnabled,
			logEvent,
			onPurchaseSuccess,
			price,
			quantity,
			submitGiftPurchase,
			user,
		],
	);

	const onSuccess = () => {
		setDidSucceed(true);
	};

	const setAndValidateEmail: AccessibleOnChange = e => {
		const { value } = e.currentTarget;

		setEmail(value);

		const isValid = validate('email', value);
		setEmailError(isValid ? '' : formatMessage(messages.emailError));
	};

	if (!appLoaded) {
		return <Loader color="gray7" />;
	}

	return (
		<CenteredRow>
			{!didSucceed && (
				<>
					<Text color="gray8" el="h2" styleAs="h4" weight={FontWeights.Medium}>
						{formatMessage(messages.provideDetails)}
					</Text>
					<Text el="h3" styleAs="p" weight={FontWeights.Regular}>
						{formatMessage(messages.afterPurchase)}
					</Text>
					<FullWidth>
						<FormInput
							name="email"
							label={formatMessage(messages.yourEmail)}
							error={emailError}
							value={email}
							onChange={setAndValidateEmail}
							isValid={isSubmittable()}
						/>
						<QuantitySelector
							quantity={quantity}
							originalPrice={originalPrice !== price ? originalPrice : undefined}
							price={price}
							offerFreeCount={offerFreeCount}
							onChangeQuantity={onChangeQuantity}
						/>
						{showB2bUpsell && <B2bUpsell />}
						<Divider />
						{isStripeNextEnabled ? (
							<PaymentElementsForm
								hideTerms
								hidePaypal
								endpoint="gift/purchase"
								onPurchaseSuccess={_onPurchaseSuccess}
								analyticsPrefix={GIFT_ANALYTICS_PREFIX}
								purchaseArgs={{ quantity, email, type: giftType === 'lifetime' ? 'lifetime' : 'annual' }}
								ctaCopy={giftType === 'lifetime' ? 'Gift Calm for Life' : formatMessage(messages.submitText)}
							/>
						) : (
							<PurchaseForm
								hideTerms
								hidePaypal
								hideApplePay
								ignoreSuccessLogging
								shouldUsePaymentFormPrice
								isDisabled={!isSubmittable()}
								updateUserOnSuccess={false}
								buttonBgColor="gradientRainbow6"
								analyticsPrefix={GIFT_ANALYTICS_PREFIX}
								onPurchase={onSubmitPayment}
								onSuccess={onSuccess}
								ctaCopy={giftType === 'lifetime' ? 'Gift Calm for Life' : formatMessage(messages.submitText)}
							/>
						)}
					</FullWidth>
					<TermsMessage>
						{formatMessage(messages.purchaseTerms, {
							termslink: (chunks: ReactNode) => (
								<b>
									<Link passHref href="/gift-terms">
										{chunks}
									</Link>
								</b>
							),
						})}
					</TermsMessage>
				</>
			)}
			{didSucceed && gifts && (
				<SuccessColumn>
					<PurchaseConfirmation gifts={gifts} email={email} giftType={giftType} />
				</SuccessColumn>
			)}
		</CenteredRow>
	);
};

export default PaymentForm;
